import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import {
  ResponsiveModal, ResponsiveModalContent, ResponsiveModalHeader, ResponsiveModalTitle, ResponsiveModalClose,
  ResponsiveModalDescription, ResponsiveModalBody,
} from '../ui/responsive-modal';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useBotModelsQuery, useMyBotsQuery } from '../../hooks/queries';
import { modelLabel } from './ModelSelect';
import { useAddProjectBot, useAssignTaskBot, useCreateBot } from '../../hooks/mutations';
import { detailToMessage } from '../../lib/apiError';
import { BOT_TEMPLATES } from '../../lib/botTemplates';
import BotFormModal from './BotFormModal';
import { EMPTY_ARRAY } from '../../lib/empty';

const scopeKey = (s) => `${s.type}:${s.id}`;

/**
 * "Meet your bots": ready-made teammates created in one click, or a custom one.
 * options.project: the new bot also joins this project (and is created in its scope).
 * options.teamId: the bot is created for this team (null: the person's own bots), as
 * where the gallery was opened from has already chosen (Settings > Bots).
 * options.taskId: the task is handed to the new bot right away.
 */
const BotGalleryModal = ({ open, options, onClose, onCreated }) => {
  const { t } = useTranslation();
  const { data } = useMyBotsQuery({ enabled: open });
  const createBot = useCreateBot();
  const addToProject = useAddProjectBot();
  const assignTask = useAssignTaskBot();
  const [scopeId, setScopeId] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);

  const project = options.project || null;
  const scopes = data?.scopes || EMPTY_ARRAY;
  const editable = scopes.filter((s) => s.can_edit);

  // A project (its team, or its owner's personal bots) or the opener fixes the scope.
  const fixedTeam = project ? project.team_id || null : options.teamId;
  const fixedScope = useMemo(() => {
    if (fixedTeam === undefined) return null;
    return fixedTeam
      ? scopes.find((s) => s.type === 'team' && s.id === fixedTeam)
      : scopes.find((s) => s.type === 'user');
  }, [fixedTeam, scopes]);

  // Pick the initial scope once per opening: the fixed one, else the person's own bots.
  // A team is only ever chosen on purpose (a team project, Settings > Bots, or the picker).
  useEffect(() => {
    if (!open) { setScopeId(null); return; }
    if (scopeId || !data) return;
    const initial = fixedTeam !== undefined ? fixedScope : data.scopes.find((s) => s.type === 'user')
      || data.scopes.find((s) => s.can_edit);
    if (initial) setScopeId(scopeKey(initial));
  }, [open, data, fixedTeam, fixedScope, scopeId]);

  const scope = scopes.find((s) => scopeKey(s) === scopeId);
  const teamId = scope?.type === 'team' ? scope.id : null;
  const canCreate = !!scope?.can_edit;
  const { data: models } = useBotModelsQuery(teamId ? { team_id: teamId } : {}, { enabled: open && !!scope });

  const finish = async (bot) => {
    if (project) await addToProject.mutateAsync({ projectId: project.id, data: { bot_id: bot.id } });
    if (options.taskId) await assignTask.mutateAsync({ taskId: options.taskId, botId: bot.id });
    toast.success(t('bots.created', { name: bot.name }));
    onCreated(bot);
  };

  const createFromTemplate = async (tpl) => {
    setBusyKey(tpl.key);
    try {
      const bot = await createBot.mutateAsync({
        name: t(`bots.templates.${tpl.key}.name`),
        description: t(`bots.templates.${tpl.key}.role`),
        persona: tpl.persona,
        avatar_color: tpl.color,
        team_id: teamId,
      });
      await finish(bot);
    } catch (err) {
      toast.error(detailToMessage(err?.response?.data?.detail) || t('common.error'));
    } finally {
      setBusyKey(null);
    }
  };

  const scopeName = (s) => (s.type === 'team' ? s.name : t('bots.scope_personal'));

  return (
    <>
      <ResponsiveModal open={open && !customOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
        <ResponsiveModalContent className="md:max-w-2xl">
          <ResponsiveModalHeader>
            <div className="flex-1 min-w-0">
              <ResponsiveModalTitle className="text-base">{t('bots.gallery_title')}</ResponsiveModalTitle>
              <ResponsiveModalDescription>{t('bots.gallery_subtitle')}</ResponsiveModalDescription>
            </div>
            <ResponsiveModalClose className="text-muted-foreground hover:bg-accent hover:text-foreground p-2" />
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            {!fixedScope && editable.length > 1 && (
              <div className="flex items-center gap-2">
                <Label htmlFor="gallery-scope" className="text-xs text-muted-foreground">{t('bots.gallery_for')}</Label>
                <Select value={scopeId || undefined} onValueChange={setScopeId}>
                  <SelectTrigger id="gallery-scope" className="w-56">
                    <SelectValue>{scope ? scopeName(scope) : ''}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {editable.map((s) => <SelectItem key={scopeKey(s)} value={scopeKey(s)}>{scopeName(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {scope && !canCreate && (
              <p className="text-xs text-muted-foreground">{t('bots.only_admins')}</p>
            )}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {BOT_TEMPLATES.map((tpl) => {
                const Icon = tpl.icon;
                return (
                  <button key={tpl.key} type="button" disabled={!canCreate || !!busyKey}
                    onClick={() => createFromTemplate(tpl)}
                    className="flex items-start gap-3 text-left rounded-xl border border-border p-3 hover:border-primary/60 hover:bg-accent/40 transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center text-foreground shrink-0" style={{ backgroundColor: tpl.color }}>
                      {busyKey === tpl.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{t(`bots.templates.${tpl.key}.name`)}</span>
                      <span className="block text-xs text-muted-foreground">{t(`bots.templates.${tpl.key}.role`)}</span>
                    </span>
                  </button>
                );
              })}
              <button type="button" disabled={!canCreate || !!busyKey} onClick={() => setCustomOpen(true)}
                className="flex items-start gap-3 text-left rounded-xl border border-dashed border-border p-3 hover:border-primary/60 hover:bg-accent/40 transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-muted text-muted-foreground shrink-0">
                  <Plus className="w-4 h-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{t('bots.gallery_custom')}</span>
                  <span className="block text-xs text-muted-foreground">{t('bots.gallery_custom_role')}</span>
                </span>
              </button>
            </div>
          </ResponsiveModalBody>
        </ResponsiveModalContent>
      </ResponsiveModal>
      <BotFormModal
        open={open && customOpen}
        onClose={() => setCustomOpen(false)}
        bot={null}
        teamId={teamId}
        models={models?.models || []}
        inheritLabel={t('bots.inherit_with', {
          model: modelLabel(models?.models, models?.inherited?.model_id) || t('bots.source_default'),
        })}
        onSaved={async (bot) => { setCustomOpen(false); await finish(bot); }}
      />
    </>
  );
};

export default BotGalleryModal;
